import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import config from "../config";
import BuzzShell, { useBuzzMe } from "./BuzzShell";

const POLL_MS = 2500;
const SOUND_KEY = "buzz_board_sound";
const WAKE_KEY = "buzz_board_wake";
const WARN_SEC = 60;
const LATE_SEC = 180;

function fmtElapsed(s) {
  const v = Math.max(0, Math.floor(s));
  return `${Math.floor(v / 60)}'${String(v % 60).padStart(2, "0")}"`;
}

const KIND_ICON = { call: "🔔", bill: "💶", order: "📝", custom: "💬" };

function CallCard({ c, sec, onAck, onDone, onUndo, t }) {
  const open = c.status === "new" || c.status === "ack";
  const level = sec > LATE_SEC ? "late" : sec > WARN_SEC ? "warn" : "";
  const [undoLeft, setUndoLeft] = useState(0);
  useEffect(() => {
    if (!undoLeft) return;
    const id = setTimeout(() => setUndoLeft(undoLeft - 1), 1000);
    return () => clearTimeout(id);
  }, [undoLeft]);

  return (
    <div className={`bz-call ${c.status} ${open ? level : ""}`}>
      <div className="bz-call-top">
        <span className="bz-call-label">{c.label}</span>
        {c.zone && <span className="bz-call-zone">{c.zone}</span>}
        {open && <span className={`bz-timer ${level}`}>{fmtElapsed(sec)}</span>}
      </div>
      <div className="bz-call-kind">
        {KIND_ICON[c.kind] || "🔔"}{" "}
        {c.kind === "bill" ? t("Bill requested") : c.kind === "order" ? t("Wants to order") : t("Waiter call")}
        {c.taps > 1 && <span className="bz-taps">×{c.taps}</span>}
      </div>
      {c.message && <div className="bz-call-msg">{c.message}</div>}
      {c.status === "ack" && (
        <div className="bz-ackby">✋ {c.ack_username || t("Taken")}</div>
      )}
      <div className="bz-call-actions">
        {c.status === "new" && (
          <button className="bz-btn primary" onClick={() => onAck(c.id)}>✋ {t("On my way")}</button>
        )}
        {open && (
          <button className="bz-btn" onClick={() => { onDone(c.id); setUndoLeft(6); }}>✓ {t("Handled")}</button>
        )}
        {c.status === "done" && undoLeft > 0 && (
          <button className="bz-btn ghost" onClick={() => { onUndo(c.id); setUndoLeft(0); }}>
            ↩ {t("Undo")} ({undoLeft})
          </button>
        )}
        {c.status === "expired" && <span className="bz-hint">⏱ {t("Missed")}</span>}
      </div>
    </div>
  );
}

function BoardInner() {
  const { t } = useTranslation();
  const { me } = useBuzzMe();
  const [data, setData] = useState({ serviceOpen: true, calls: [] });
  const [sessions, setSessions] = useState([]);
  const [tables, setTables] = useState([]);
  const [qrModal, setQrModal] = useState(null); // {id, label, url}
  const [pickTables, setPickTables] = useState(null); // null | Set(ids)
  const [mineOnly, setMineOnly] = useState(null); // null = δεν αποφασίστηκε ακόμα
  const [live, setLive] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [sound, setSound] = useState(() => localStorage.getItem(SOUND_KEY) !== "0");
  const [wake, setWake] = useState(() => localStorage.getItem(WAKE_KEY) === "1");
  const timerRef = useRef(null);
  // Οι χρόνοι αναμονής έρχονται από τον server (age_sec) — τα ρολόγια βάσης,
  // API και συσκευής δεν συμφωνούν και ο τοπικός υπολογισμός έβγαζε πάντα 0.
  const baseAtRef = useRef(Date.now());
  const seenRef = useRef(null);
  const filterRef = useRef(null);
  const audioRef = useRef(null);

  // Default: σερβιτόροι βλέπουν τα δικά τους, ο admin τα πάντα
  useEffect(() => {
    if (me && mineOnly === null) setMineOnly(!me.isAdmin);
  }, [me, mineOnly]);

  // ── Ήχος: ξεκλειδώνεται με το πρώτο άγγιγμα (πολιτική autoplay) ──────────
  useEffect(() => {
    audioRef.current = new Audio("/alarm.wav");
    audioRef.current.preload = "auto";
    const unlock = () => {
      const a = audioRef.current;
      if (!a) return;
      a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
      window.removeEventListener("pointerdown", unlock);
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const alertNewCall = useCallback(() => {
    if (navigator.vibrate) { try { navigator.vibrate([220, 120, 220]); } catch {} }
    if (!sound || !audioRef.current) return;
    try { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); } catch {}
  }, [sound]);

  // ── Οθόνη αναμμένη (tablet στο πάσο) ────────────────────────────────────
  useEffect(() => {
    let lock = null;
    const acquire = async () => {
      if (!wake || !("wakeLock" in navigator) || document.hidden) return;
      try { lock = await navigator.wakeLock.request("screen"); } catch {}
    };
    const onVis = () => { if (!document.hidden) acquire(); };
    acquire();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      try { if (lock) lock.release(); } catch {}
      lock = null;
    };
  }, [wake]);

  const load = useCallback(async () => {
    if (mineOnly === null) return;
    try {
      const [{ data: board }, { data: sess }] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/buzz/board`, { params: { mine: mineOnly ? 1 : 0 } }),
        axios.get(`${config.API_BASE_URL}/buzz/sessions`),
      ]);
      baseAtRef.current = Date.now();
      setData(board);
      setSessions(sess);
      setLive(true);

      // Νέα κλήση που δεν είχαμε ξαναδεί → ήχος + δόνηση. Όχι στο πρώτο
      // φόρτωμα ούτε όταν μόλις άλλαξε το φίλτρο (οι κλήσεις «εμφανίζονται»
      // λόγω του φίλτρου, δεν είναι καινούριες).
      const ids = new Set(board.calls.filter(c => c.status === "new").map(c => c.id));
      const sameFilter = filterRef.current === mineOnly;
      filterRef.current = mineOnly;
      if (sameFilter && seenRef.current && [...ids].some(id => !seenRef.current.has(id))) alertNewCall();
      seenRef.current = ids;
    } catch {
      setLive(false);
    }
  }, [mineOnly, alertNewCall]);

  useEffect(() => {
    load();
    axios.get(`${config.API_BASE_URL}/buzz/tables`).then(r => setTables(r.data)).catch(() => {});
    timerRef.current = setInterval(load, POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  // ρολόι 1", ώστε οι χρόνοι αναμονής να τρέχουν ανάμεσα στα polls
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const elapsed = useCallback(
    (c) => (Number(c.age_sec) || 0) + (now - baseAtRef.current) / 1000,
    [now]
  );

  const act = (path) => axios.post(`${config.API_BASE_URL}${path}`).then(load).catch(load);

  const toggleService = async () => {
    await axios.put(`${config.API_BASE_URL}/buzz/board/service-open`, { open: !data.serviceOpen });
    load();
  };

  const createSession = async () => {
    const ids = [...pickTables];
    if (!ids.length) return;
    const { data: s } = await axios.post(`${config.API_BASE_URL}/buzz/sessions`, { tableIds: ids });
    setPickTables(null);
    setQrModal({ sessionId: s.id, label: t("Guest QR"), url: s.publicUrl });
    load();
  };

  const openCalls = useMemo(() => data.calls.filter(c => c.status === "new" || c.status === "ack"), [data]);
  const recentClosed = useMemo(() => data.calls.filter(c => c.status === "done" || c.status === "expired"), [data]);
  const waiting = openCalls.filter(c => c.status === "new").length;
  const fmtTime = (d) => new Date(d).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" });

  // Ο τίτλος της καρτέλας δείχνει πόσες κλήσεις περιμένουν (ορατό και σε
  // background tab / δεύτερη οθόνη)
  useEffect(() => {
    const original = document.title;
    document.title = openCalls.length ? `(${openCalls.length}) Buzz` : "Buzz";
    return () => { document.title = original; };
  }, [openCalls.length]);

  const toggleSound = () => setSound(v => { localStorage.setItem(SOUND_KEY, v ? "0" : "1"); return !v; });
  const toggleWake = () => setWake(v => { localStorage.setItem(WAKE_KEY, v ? "0" : "1"); return !v; });

  return (
    <>
      {data.notifiableStaff === 0 && (
        <div className="bz-banner err">
          ⛔ <b>{t("Service is inactive")}</b> — {t("nobody on the team receives notifications. Guests see the service as unavailable. Fix it in the")}{" "}
          <a href="/buzz/team">{t("Team")}</a> {t("tab.")}
        </div>
      )}

      <div className="bz-bar">
        <div className="bz-toolbar">
          <span className={`bz-count ${openCalls.length ? "" : "zero"}`}>{openCalls.length}</span>
          <b style={{ fontSize: 15 }}>{t("Open calls")}</b>
          {waiting > 0 && <span className="bz-chip" style={{ background: "#fef3c7", color: "#92400e", fontWeight: 700 }}>
            {waiting} {t("waiting")}
          </span>}
          <span style={{ flex: 1 }} />
          <span className={`bz-live ${live ? "" : "off"}`}><i />{live ? t("live") : t("offline")}</span>
          <button className={`bz-icon-btn ${sound ? "on" : ""}`} onClick={toggleSound}
            title={sound ? t("Sound on") : t("Sound off")} aria-label={t("Sound")}>
            {sound ? "🔊" : "🔇"}
          </button>
          <button className={`bz-icon-btn ${wake ? "on" : ""}`} onClick={toggleWake}
            title={t("Keep the screen awake")} aria-label={t("Keep the screen awake")}>
            {wake ? "💡" : "🌙"}
          </button>
        </div>

        <div className="bz-toolbar" style={{ marginTop: 8 }}>
          <label className="bz-toggle" style={data.notifiableStaff === 0 ? { opacity: 0.45 } : undefined}>
            <input type="checkbox" checked={!!data.serviceOpen && data.notifiableStaff > 0}
              disabled={data.notifiableStaff === 0} onChange={toggleService} />
            {data.notifiableStaff === 0 ? t("Service is inactive")
              : data.serviceOpen ? t("Accepting calls") : t("Calls paused")}
          </label>
          <span style={{ flex: 1 }} />
          <div className="bz-seg">
            <button className={mineOnly ? "on" : ""} onClick={() => setMineOnly(true)}>👤 {t("My zones")}</button>
            <button className={mineOnly ? "" : "on"} onClick={() => setMineOnly(false)}>{t("All calls")}</button>
          </div>
          <button className="bz-btn primary" onClick={() => setPickTables(new Set())}>➕ {t("Guest QR")}</button>
        </div>
      </div>

      {mineOnly && data.myFilter && !data.myFilter.allZones && (
        <div className="bz-hint" style={{ marginBottom: 10 }}>
          👤 {t("Showing calls for")}: {data.myFilter.zoneNames.length
            ? data.myFilter.zoneNames.map(z => <span key={z} className="bz-chip" style={{ marginRight: 6 }}>{z}</span>)
            : <b style={{ color: "#dc2626" }}>{t("no zones — ask your admin")}</b>}
        </div>
      )}

      {openCalls.length === 0 && (
        <div className="bz-empty">
          <div style={{ fontSize: 40, marginBottom: 8 }}>🧘</div>
          {t("No open calls")}
        </div>
      )}
      <div className="bz-grid">
        {openCalls.map(c => (
          <CallCard key={c.id} c={c} t={t} sec={elapsed(c)}
            onAck={(id) => act(`/buzz/calls/${id}/ack`)}
            onDone={(id) => act(`/buzz/calls/${id}/done`)}
            onUndo={(id) => act(`/buzz/calls/${id}/undo`)} />
        ))}
      </div>

      {sessions.length > 0 && (
        <div className="bz-card">
          <h4 style={{ marginBottom: 10 }}>{t("Open guest QRs")}</h4>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {sessions.map(s => (
              <div key={s.id} style={{
                border: "1px solid #e2e8f0", borderRadius: 14, padding: "10px 14px",
                display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
              }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 17 }}>🍽 {s.tables_label}</div>
                  <div className="bz-hint">
                    🕐 {fmtTime(s.created_at)} → {t("expires")} {fmtTime(s.expires_at)}
                  </div>
                </div>
                <button className="bz-btn"
                  onClick={() => setQrModal({ sessionId: s.id, label: s.tables_label, url: s.publicUrl })}>
                  📱 {t("Show QR")}
                </button>
                <button className="bz-btn ghost" style={{ color: "#dc2626" }}
                  onClick={() => window.confirm(`${t("Close guest QR for")} ${s.tables_label};`) &&
                    axios.post(`${config.API_BASE_URL}/buzz/sessions/${s.id}/close`).then(load)}>
                  ✕ {t("Close")}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {recentClosed.length > 0 && (
        <>
          <h4 style={{ margin: "22px 0 8px", color: "#64748b", fontSize: 14, textTransform: "uppercase", letterSpacing: ".5px" }}>
            {t("Recently closed")}
          </h4>
          <div className="bz-grid">
            {recentClosed.map(c => (
              <CallCard key={c.id} c={c} t={t} sec={elapsed(c)}
                onAck={() => {}} onDone={() => {}}
                onUndo={(id) => act(`/buzz/calls/${id}/undo`)} />
            ))}
          </div>
        </>
      )}

      {pickTables && (
        <div className="bz-modal-bg" onClick={() => setPickTables(null)}>
          <div className="bz-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t("Guest QR — pick tables")}</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", margin: "14px 0" }}>
              {tables.filter(tb => tb.active).map(tb => (
                <button key={tb.id}
                  className={`bz-btn ${pickTables.has(tb.id) ? "primary" : ""}`}
                  onClick={() => {
                    const next = new Set(pickTables);
                    next.has(tb.id) ? next.delete(tb.id) : next.add(tb.id);
                    setPickTables(next);
                  }}>
                  {tb.label}
                </button>
              ))}
            </div>
            <button className="bz-btn primary" disabled={!pickTables.size} onClick={createSession}>
              {t("Create QR")}
            </button>
          </div>
        </div>
      )}

      {qrModal && (
        <div className="bz-modal-bg" onClick={() => setQrModal(null)}>
          <div className="bz-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{qrModal.label}</h3>
            <img alt="QR"
              src={`${config.API_BASE_URL}/buzz/sessions/${qrModal.sessionId}/qr.png?ts=${Date.now()}`} />
            <div className="bz-qr-url">{qrModal.url}</div>
            <button className="bz-btn" style={{ marginTop: 12 }} onClick={() => setQrModal(null)}>{t("Close")}</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function BuzzBoard() {
  return <BuzzShell><BoardInner /></BuzzShell>;
}
